import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../contexts/PageLoaderContext';
import apiService from '../../services/api';
import SkeletonLoader from '../UI/SkeletonLoader';
import EmptyState from '../UI/EmptyState';
import PerformanceDashboard from '../Admin/PerformanceDashboard/PerformanceDashboard';
import styles from '../../styles/Profile.module.css';
import buttonStyles from '../../styles/Buttons.module.css';

interface UserProfileData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  mobile_number?: string;
  designation?: string;
  role: string;
  avatar?: string;
  date_joined: string;
  is_active: boolean;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, refreshUser } = useAuth();
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    designation: '',
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    if (!authUser) return;

    setLoading(true);
    showLoader('Loading profile...');
    try {
      // Get current user data
      const userData: UserProfileData = {
        id: authUser.id,
        email: authUser.email,
        first_name: authUser.first_name || '',
        last_name: authUser.last_name || '',
        full_name: authUser.first_name && authUser.last_name
          ? `${authUser.first_name} ${authUser.last_name}`
          : authUser.email,
        phone: (authUser as any).mobile_number || (authUser as any).phone || '',
        designation: authUser.designation || '',
        role: authUser.role || 'member',
        avatar: (authUser as any).avatar || (authUser as any).profile_picture || '',
        date_joined: (authUser as any).date_joined || '',
        is_active: authUser.is_active !== false,
      };

      setProfileData(userData);
      setFormData({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        phone: userData.phone || '',
        designation: userData.designation || '',
      });
      setAvatarPreview(userData.avatar || null);
    } catch (error: any) {
      console.error('Error loading profile:', error);
      notificationService.error('Failed to load profile data');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        notificationService.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        notificationService.error('Image size must be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData) return;

    setSaving(true);
    showLoader('Updating profile...');
    try {
      const response = await apiService.updateUser({
        user_id: profileData.id,
        full_name: `${formData.first_name} ${formData.last_name}`.trim(),
        email: formData.email,
        phone: formData.phone,
        designation: formData.designation,
      });

      if (response.status === 'success') {
        notificationService.success('Profile updated successfully');
        setEditMode(false);

        // Refresh user data in auth context
        if (refreshUser) {
          await refreshUser();
        }

        await loadProfileData();
      } else {
        notificationService.error(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update profile';
      notificationService.error(errorMessage);
    } finally {
      setSaving(false);
      hideLoader();
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      notificationService.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      notificationService.error('Password must be at least 8 characters long');
      return;
    }

    setChangingPassword(true);
    showLoader('Changing password...');
    try {
      const response = await apiService.changePassword(passwordData);

      if (response.status === 'success') {
        notificationService.success('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
      } else {
        notificationService.error(response.message || 'Failed to change password');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to change password. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      setChangingPassword(false);
      hideLoader();
    }
  };

  const formatDesignation = (designation?: string) => {
    if (!designation) return 'N/A';

    return designation
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = () => {
    if (!profileData) return 'U';
    const first = profileData.first_name?.charAt(0)?.toUpperCase() || '';
    const last = profileData.last_name?.charAt(0)?.toUpperCase() || '';
    return (first + last) || profileData.email.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className={styles.profileContainer}>
        <SkeletonLoader count={1} variant="card" />
        <div style={{ marginTop: '2rem' }}>
          <SkeletonLoader count={2} variant="card" />
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className={styles.profileContainer}>
        <EmptyState
          image="/images/notasks.png"
          title="Profile Not Found"
          description="Unable to load your profile information. Please try again."
        />
      </div>
    );
  }

  return (
    <div className={styles.profileContainer}>
      {/* Page Header */}

      {/* Performance Dashboard */}
      {authUser?.id && (
        <PerformanceDashboard
          userId={authUser.id}
          excludeTaskManagement={true}
          hideBreadcrumb={true}
        />
      )}
    </div>
  );
};

export default Profile;

